import React, { useState, useEffect, useCallback } from 'react';
import { Trophy, ChevronDown, ChevronUp, RefreshCw, Star, AlertTriangle, Sparkles } from 'lucide-react';
import { authFetch } from '../lib/authFetch';

/**
 * AiRecipeLeaderboard
 * -------------------
 * SuperAdmin dashboard card showing aggregated, anonymous feedback on the
 * AI Chef recipes beta testers have saved.  Three sections:
 *   - Top rated   — best blended score (tasty + simple + cook-again%)
 *   - Needs attention — bottom of the rated list (candidates to retire / iterate)
 *   - Saved but not rated — saved by users but no stars yet
 * Backed by GET /api/admin/ai-recipes/leaderboard.
 */
export default function AiRecipeLeaderboard() {
  const apiBase = process.env.REACT_APP_BACKEND_URL;
  const [data, setData] = useState(null);
  const [collapsed, setCollapsed] = useState(true);
  const [refreshing, setRefreshing] = useState(false);

  const load = useCallback(async () => {
    setRefreshing(true);
    try {
      const r = await authFetch(`${apiBase}/api/admin/ai-recipes/leaderboard`);
      if (r.ok) setData(await r.json());
    } catch (_) { /* swallow */ }
    setRefreshing(false);
  }, [apiBase]);

  useEffect(() => { load(); }, [load]);

  if (!data) {
    return (
      <div className="mb-4 bg-zinc-900 border border-zinc-800 rounded-2xl p-4 text-xs text-zinc-500" data-testid="ai-leaderboard-loading">
        Loading AI Chef leaderboard…
      </div>
    );
  }

  const {
    total_ai_recipes_saved = 0,
    total_ratings_collected = 0,
    top_rated = [],
    needs_attention = [],
    saved_but_not_rated = [],
  } = data;

  return (
    <div className="mb-4 bg-zinc-900 border border-zinc-800 rounded-2xl overflow-hidden" data-testid="ai-leaderboard">
      <button
        onClick={() => setCollapsed((v) => !v)}
        className="w-full flex items-center justify-between px-4 py-3 hover:bg-zinc-800/50 transition-colors"
        data-testid="ai-leaderboard-toggle"
      >
        <div className="flex items-center gap-3 min-w-0">
          <Trophy className="w-4 h-4 text-violet-400 flex-shrink-0" />
          <span className="text-[11px] font-bold uppercase tracking-[0.18em] text-zinc-300">Tudor&apos;s AI Chef Leaderboard</span>
          <span className="text-[11px] text-zinc-500 truncate">
            <span className="font-bold text-white">{total_ai_recipes_saved}</span> saved ·
            <span className="font-bold text-white"> {total_ratings_collected}</span> ratings
          </span>
        </div>
        <div className="flex items-center gap-2 flex-shrink-0">
          <button
            onClick={(e) => { e.stopPropagation(); load(); }}
            className="p-1 rounded hover:bg-zinc-700"
            title="Refresh"
            data-testid="ai-leaderboard-refresh"
          >
            <RefreshCw className={`w-3.5 h-3.5 text-zinc-400 ${refreshing ? 'animate-spin' : ''}`} />
          </button>
          {collapsed ? <ChevronDown className="w-4 h-4 text-zinc-500" /> : <ChevronUp className="w-4 h-4 text-zinc-500" />}
        </div>
      </button>

      {!collapsed && (
        <div className="px-4 pb-4 pt-1 border-t border-zinc-800 space-y-4" data-testid="ai-leaderboard-expanded">
          {total_ai_recipes_saved === 0 && (
            <p className="text-[12px] text-zinc-500 italic">
              No AI recipes have been saved by beta testers yet.  This widget populates as users tap the &ldquo;Save&rdquo; star on AI Chef recipes.
            </p>
          )}

          {top_rated.length > 0 && (
            <Section
              title="Top rated"
              icon={<Sparkles className="w-3 h-3 text-amber-400" />}
              colour="amber"
              recipes={top_rated}
            />
          )}

          {needs_attention.length > 0 && (
            <Section
              title="Needs attention (lowest rated)"
              icon={<AlertTriangle className="w-3 h-3 text-rose-400" />}
              colour="rose"
              recipes={needs_attention}
            />
          )}

          {saved_but_not_rated.length > 0 && (
            <Section
              title="Saved: no rating yet"
              icon={<Star className="w-3 h-3 text-zinc-400" />}
              colour="zinc"
              recipes={saved_but_not_rated}
              hideRatings
            />
          )}
        </div>
      )}
    </div>
  );
}

function Section({ title, icon, colour, recipes, hideRatings }) {
  return (
    <div>
      <div className="text-[10px] font-bold uppercase tracking-[0.2em] text-zinc-500 mb-2 flex items-center gap-1.5">
        {icon} {title}
      </div>
      <div className="space-y-1.5">
        {recipes.map((r) => (
          <div
            key={r.id}
            className={`flex items-center justify-between text-[12px] bg-zinc-800/50 px-3 py-2 rounded-lg border border-${colour}-900/30`}
            data-testid={`leaderboard-row-${r.id}`}
          >
            <div className="flex-1 min-w-0">
              <div className="text-white font-semibold truncate" title={r.title}>{r.title || '(untitled)'}</div>
              <div className="text-[10px] text-zinc-500 mt-0.5">
                <span className="capitalize">{r.category || '—'}</span>
                {r.country && <> · {r.country}</>}
                {' · '}
                <span className="text-zinc-400">{r.saves} {r.saves === 1 ? 'save' : 'saves'}</span>
              </div>
            </div>
            {!hideRatings && (
              <div className="flex items-center gap-3 text-[10px] tabular-nums flex-shrink-0 ml-2">
                {r.avg_tasty > 0 && (
                  <span className="text-amber-300 flex items-center gap-0.5">
                    <Star className="w-3 h-3 fill-amber-400 text-amber-400" />
                    {r.avg_tasty.toFixed(1)}
                  </span>
                )}
                {r.avg_simple > 0 && (
                  <span className="text-emerald-300" title="Simple to prepare">
                    🛠 {r.avg_simple.toFixed(1)}
                  </span>
                )}
                {r.cook_again_pct != null && (
                  <span className={r.cook_again_pct >= 60 ? 'text-emerald-400' : (r.cook_again_pct < 30 ? 'text-rose-400' : 'text-zinc-400')}>
                    {r.cook_again_pct}% redo
                  </span>
                )}
              </div>
            )}
          </div>
        ))}
      </div>
    </div>
  );
}
